import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import { TST_COLUMN_SIZE, TST_DEFAULT_PAGE_SIZE } from "@impero-com/ui";
import {
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";

import {
    TestingProgramForUserListDto as TestingProgramsForUser,
    StandardAccessRight,
} from "api/generated/model";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { WebRoute } from "routes";
import { canViewTestingProgram } from "utils/actions";
import { mapAsResourceWithoutAction } from "utils/dtoMapping";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";

import {
    TESTING_PROGRAM_LIST_COLUMN_IDS,
    TESTING_PROGRAM_TABLE_STATE_STORAGE_KEY,
    testingProgramListColumnFiltersCodecMap,
    testingProgramListTableDefaultValues,
} from "./consts";

export interface TestingProgramRow {
    id: number;
    name: string;
    accessRight: StandardAccessRight;
}

export function useUserTestingProgramListTable(data: TestingProgramsForUser) {
    const { t } = useTranslation();

    const tableData = useMemo(
        () => mapAsResourceWithoutAction(data.content),
        [data]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");

    const stateAndOnChanges = usePresetTableSearchParams(
        TESTING_PROGRAM_TABLE_STATE_STORAGE_KEY,
        testingProgramListTableDefaultValues,
        testingProgramListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = buildColumns(t, data);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => row.id.toString(),
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<TestingProgramRow>(),
        getSortedRowModel: getSortedRowModel<TestingProgramRow>(),
        getFilteredRowModel: getFilteredRowModel<TestingProgramRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<TestingProgramRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<TestingProgramRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (column: Column<TestingProgramRow>) =>
        getFilterComponent(t, column);

    return {
        table,
        getFilterComponent: filterComponent,
        tableId: TESTING_PROGRAM_TABLE_STATE_STORAGE_KEY,
    };
}

const columnHelper = createColumnHelper<TestingProgramRow>();
function buildColumns(t: TFunction, testingPrograms: TestingProgramsForUser) {
    return [
        columnHelper.accessor((row) => row.name, {
            id: TESTING_PROGRAM_LIST_COLUMN_IDS.NAME,
            cell: (info) => {
                const row = info.row.original;
                const actions = testingPrograms.content.find(
                    (dto) => dto.resource.id === row.id
                )?.actions;

                const hasAccess = actions
                    ? canViewTestingProgram(actions)
                    : false;
                return (
                    <ProtectedLink
                        to={`${WebRoute.TESTING_PROGRAMS}/${row.id}`}
                        hasAccess={hasAccess}
                    >
                        {row.name}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingProgramRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        TESTING_PROGRAM_LIST_COLUMN_IDS.NAME,
                        value
                    )
            ),
            header: () =>
                t("AccessAndAssignment_TestingPrograms_TestingProgram"),
            size: TST_COLUMN_SIZE.XL,
        }),
        columnHelper.accessor((row) => row.accessRight, {
            id: TESTING_PROGRAM_LIST_COLUMN_IDS.ACCESS_RIGHT,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumeric",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TestingProgramRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        TESTING_PROGRAM_LIST_COLUMN_IDS.ACCESS_RIGHT,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_TestingPrograms_AccessRight"),
        }),
    ];
}

type TestingProgramListColumnFilters = ZodCodecMapToType<
    typeof testingProgramListColumnFiltersCodecMap
>;

type TestingProgramColumnId =
    (typeof TESTING_PROGRAM_LIST_COLUMN_IDS)[keyof typeof TESTING_PROGRAM_LIST_COLUMN_IDS];

function getFilterComponent(t: TFunction, column: Column<TestingProgramRow>) {
    const getFilterHelpers =
        createFilterHelpersGetter<TestingProgramListColumnFilters>(column);
    const columnId = column.id as TestingProgramColumnId;

    switch (columnId) {
        case TESTING_PROGRAM_LIST_COLUMN_IDS.NAME: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((program) => ({
                label: program,
                value: program,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        "UserTestingProgramList_TestProgramFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case TESTING_PROGRAM_LIST_COLUMN_IDS.ACCESS_RIGHT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((program) => ({
                label: program,
                value: program,
            }));
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(
                        "UserTestingProgramList_AccessRightFilterPlaceholder"
                    )}
                    setFilterValue={setFilterValue}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
